import React from 'react';

import { formatToPercents } from 'app/pages/loanApplication/LoanApplication.utils';
import {
  DollarIcon,
  PercentageIcon,
  WalletIcon,
} from 'app/shared/componentsRestyled/coreDesignSystem/icons';

import { StatusColor, StatusEnum } from 'constants/status';
import { getConditionalArray, getConditionalObject } from 'utils/data';

import PitiData from '../../PitiData';
import { PITI_PENDING_CHANGES_KEY } from '../../PitiData/PitiData.constants';

import HousingExpenseData from './components/HousingExpenseData';
import {
  LOAN_AMOUNT_CHART_LEGEND_FIELDS,
  LOAN_TYPE_PROGRAM_FIELDS,
  LTV_CHART_LEGEND_FIELDS,
  MetricCardsName,
  MONTHLY_PAYMENT_CHART_LEGEND_FIELDS,
  MONTHLY_PAYMENT_PROGRAM_FIELDS,
  MONTHLY_PAYMENT_SUBJECT_PROPERTY_FIELDS,
} from './MetricCards.constants';
import { pitiTooltipStyles } from './MetricCards.styles';
import {
  formatCurrency,
  getChattelHousingExpense,
  getChattelMonthlyPaymentValue,
  getLoanAmountValue,
  getMonthlyPaymentValue,
  getPendingChangeSourcesChangeKind,
} from './MetricCards.utils';

// LEN-204: a chattel loan is priced on a real LTV (the home secures it), so only an unsecured loan hides it.
export const getMetricCardsConfig = (
  metrics,
  {
    isUnsecured = false,
    isChattel = false,
    subjectPropertyData = null,
    chattelDetails = null,
  } = {},
) => {
  // An unpriced loan (PITI null/0) or a not-yet-loaded summary has no breakdown worth showing.
  const isPitiHoverShown = isChattel && subjectPropertyData?.piti > 0;
  const chattelHousingExpense = getChattelHousingExpense({ piti: metrics?.piti, chattelDetails });
  // No lot rent or land payment means the housing expense is just the PITI on Monthly Payment.
  const isHousingExpenseShown = isChattel && chattelHousingExpense.siteExpenses > 0;

  const {
    chartLegendModifiedFields,
    programModifiedFields,
    subjectPropertyModifiedFields,
    chartLegendModifiedFieldDetails = null,
    programModifiedFieldDetails = null,
    subjectPropertyModifiedFieldDetails = null,
  } = metrics ?? {};

  const chartLegendPendingFields = {
    modifiedFields: chartLegendModifiedFields,
    modifiedFieldDetails: chartLegendModifiedFieldDetails,
  };
  const programPendingFields = {
    modifiedFields: programModifiedFields,
    modifiedFieldDetails: programModifiedFieldDetails,
  };
  const subjectPropertyPendingFields = {
    modifiedFields: subjectPropertyModifiedFields,
    modifiedFieldDetails: subjectPropertyModifiedFieldDetails,
  };

  const loanAmountSources = [
    { ...chartLegendPendingFields, fieldNames: LOAN_AMOUNT_CHART_LEGEND_FIELDS },
  ];
  const loanTypeSources = [{ ...programPendingFields, fieldNames: LOAN_TYPE_PROGRAM_FIELDS }];
  const ltvSources = [{ ...chartLegendPendingFields, fieldNames: LTV_CHART_LEGEND_FIELDS }];
  const chattelPitiSources = getConditionalArray(isChattel, () => [
    { ...subjectPropertyPendingFields, fieldNames: [PITI_PENDING_CHANGES_KEY] },
  ]);
  const monthlyPaymentSources = [
    { ...subjectPropertyPendingFields, fieldNames: MONTHLY_PAYMENT_SUBJECT_PROPERTY_FIELDS },
    { ...programPendingFields, fieldNames: MONTHLY_PAYMENT_PROGRAM_FIELDS },
    { ...chartLegendPendingFields, fieldNames: MONTHLY_PAYMENT_CHART_LEGEND_FIELDS },
    ...chattelPitiSources,
  ];

  return [
    {
      name: MetricCardsName.LOAN_AMOUNT,
      value: getLoanAmountValue(metrics),
      icon: <DollarIcon />,
      iconColor: StatusColor[StatusEnum.SUCCESS],
      changeKind: getPendingChangeSourcesChangeKind(loanAmountSources),
      pendingChangeSources: loanAmountSources,
    },
    {
      name: MetricCardsName.LOAN_TYPE,
      value: metrics?.loanType,
      icon: <WalletIcon />,
      changeKind: getPendingChangeSourcesChangeKind(loanTypeSources),
      pendingChangeSources: loanTypeSources,
    },
    ...getConditionalArray(!isUnsecured, () => [
      {
        name: MetricCardsName.LTV_CLTV_HLTV,
        value: `${formatToPercents(metrics?.ltv)} / ${formatToPercents(
          metrics?.cltv,
        )} / ${formatToPercents(metrics?.hltv)}`,
        icon: <PercentageIcon />,
        iconColor: StatusColor[StatusEnum.WARNING],
        changeKind: getPendingChangeSourcesChangeKind(ltvSources),
        pendingChangeSources: ltvSources,
      },
    ]),
    {
      name: MetricCardsName.MONTHLY_PAYMENT,
      value: isChattel ? getChattelMonthlyPaymentValue(metrics) : getMonthlyPaymentValue(metrics),
      icon: <DollarIcon />,
      iconColor: StatusColor[StatusEnum.ERROR],
      changeKind: getPendingChangeSourcesChangeKind(monthlyPaymentSources),
      pendingChangeSources: monthlyPaymentSources,
      ...getConditionalObject(isPitiHoverShown, () => ({
        withInfoIcon: true,
        hoverContent: <PitiData data={subjectPropertyData} />,
        customTooltipStyles: pitiTooltipStyles,
      })),
    },
    ...getConditionalArray(isHousingExpenseShown, () => [
      {
        name: MetricCardsName.HOUSING_EXPENSE,
        value: formatCurrency(chattelHousingExpense.housingExpense),
        icon: <WalletIcon />,
        iconColor: StatusColor[StatusEnum.ERROR],
        changeKind: getPendingChangeSourcesChangeKind(chattelPitiSources),
        pendingChangeSources: chattelPitiSources,
        withInfoIcon: true,
        hoverContent: <HousingExpenseData data={chattelHousingExpense} />,
        customTooltipStyles: pitiTooltipStyles,
      },
    ]),
  ];
};
